<template>
  <div class="app">
    <!-- Panel de perfil (controlado por usePerfilPanel composable) -->
    <PerfilPanel :mostrar="panelAbierto" @cerrar="cerrar" />

    <!-- Banner de instalación PWA -->
    <div v-if="mostrarBanner()" class="install-banner">
      <div class="install-banner-content">
        <div class="install-banner-texto">
          <strong>Instalar Vedent</strong>
          <span v-if="!esIOS">Acceso rápido desde tu pantalla de inicio</span>
          <span v-else>Agregá Vedent a tu pantalla de inicio</span>
        </div>
        <div class="install-banner-acciones">
          <!-- Android/Chrome: botón directo -->
          <button v-if="puedeInstalar" class="btn-instalar" @click="instalar">
            Instalar
          </button>
          <!-- iOS: instrucciones manuales -->
          <button v-else-if="esIOS" class="btn-instalar" @click="mostrarInstruccionesIOS = true">
            Cómo instalar
          </button>
          <button class="btn-descartar" @click="descartarBanner">✕</button>
        </div>
      </div>
    </div>

    <main class="content">
      <slot />
    </main>
    <BottomNavigation />

    <!-- Toast global de errores de escritura -->
    <ToastNotification
      v-if="toastMensaje"
      :message="toastMensaje"
      :type="toastTipo"
      :duration="4500"
      @close="cerrarToast"
    />

    <!-- Modal instrucciones iOS -->
    <Teleport to="body">
      <div v-if="mostrarInstruccionesIOS" class="modal-overlay" @click.self="mostrarInstruccionesIOS = false">
        <div class="modal-ios">
          <button class="btn-cerrar-ios" @click="mostrarInstruccionesIOS = false">✕</button>
          <h3>Instalar Vedent en iPhone/iPad</h3>
          <ol class="pasos-ios">
            <li>
              <span class="paso-num">1</span>
              <span>Toca el botón <strong>Compartir</strong> en la barra de Safari (el ícono con la flecha hacia arriba)</span>
            </li>
            <li>
              <span class="paso-num">2</span>
              <span>Desplazate hacia abajo y seleccioná <strong>"Agregar a pantalla de inicio"</strong></span>
            </li>
            <li>
              <span class="paso-num">3</span>
              <span>Tocá <strong>"Agregar"</strong> y listo</span>
            </li>
          </ol>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref } from "vue";
import BottomNavigation from "@/components/BottomNavigation.vue";
import PerfilPanel from "@/components/PerfilPanel.vue";
import { useInstallPrompt } from "@/composables/useInstallPrompt";
import { usePerfilPanel } from "@/composables/usePerfilPanel";
import ToastNotification from "@/components/ToastNotification.vue";
import { useToast } from "@/composables/useToast";
const { puedeInstalar, esIOS, instalar, descartarBanner, mostrarBanner } = useInstallPrompt();
const mostrarInstruccionesIOS = ref(false);
const { panelAbierto, cerrar } = usePerfilPanel();
const { mensaje: toastMensaje, tipo: toastTipo, cerrar: cerrarToast } = useToast();
</script>

<style scoped>
.app {
  padding-bottom: 100px;
}

.content {
  min-height: 100vh;
}

/* Banner de instalación */
.install-banner {
  background: linear-gradient(135deg, #678AB2 0%, #4d6f96 100%);
  color: white;
  padding: 12px 16px;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(103, 138, 178, 0.3);
}

.install-banner-content {
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.install-banner-texto {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.install-banner-texto strong {
  font-size: 15px;
}

.install-banner-texto span {
  font-size: 13px;
  opacity: 0.9;
}

.install-banner-acciones {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.btn-instalar {
  background: white;
  color: #678AB2;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-instalar:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.btn-descartar {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
}

.btn-descartar:hover {
  color: white;
}

/* Modal iOS */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
}

.modal-ios {
  background: white;
  border-radius: 16px;
  padding: 28px 24px;
  max-width: 380px;
  width: 100%;
  position: relative;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.2s ease-out;
}

@keyframes slideIn {
  from { transform: translateY(-20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.btn-cerrar-ios {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 20px;
  color: #999;
  cursor: pointer;
}

.modal-ios h3 {
  font-size: 18px;
  color: #111827;
  margin: 0 0 20px 0;
}

.pasos-ios {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.pasos-ios li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: #4b5563;
  font-size: 15px;
  line-height: 1.5;
}

.paso-num {
  background: #678AB2;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
}

/* Tablet y Desktop */
@media (min-width: 768px) {
  .app {
    display: flex;
    min-height: 100vh;
  }

  .content {
    flex: 1;
    min-height: 100vh;
  }

  .install-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
  }
}
</style>
